import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import type { SocialGridBlock as Value } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { getSpacingClasses } from "../spacing";

import styles from "./index.module.scss";

type Props = Immutable<{
    value: Value;
}>;

export const SocialGridBlock = (props: Props): React.JSX.Element => {
    const v = props.value;
    return (
        <div
            className={concatClassNames([
                // `.social-grid` stays global: `styles/snippets/_social-grid-item.scss`
                // (outside this ticket's `styles/blocks/` scope) still selects
                // `.social-grid .item`.
                "social-grid",
                styles.socialGrid,
                getSpacingClasses(v.spacing),
            ])}
        >
            <header>
                <RichTextBlock value={v.title ?? ""} />
            </header>
            <ul>
                {(v.social_grid ?? []).map((item, i) => {
                    // Legacy template rendered `{% image block.image fill-500x500 %}`
                    // and `{% image block.social_network_icon fill-32x32 %}`. The
                    // snippet now serializes those exact fill specs, so read the
                    // rendition URLs directly. Both images are nullable on the
                    // model; guard them (the fixture supplies images, but
                    // production data may not).
                    const imageUrl =
                        item.image?.renditions["fill-500x500"]?.url;
                    const icon = item.social_network_icon;
                    const iconUrl = icon?.renditions["fill-32x32"]?.url;
                    return (
                        <li key={i}>
                            <a
                                href={item.link_to_post}
                                target="_blank"
                                rel="noreferrer"
                                className={concatClassNames([
                                    "item",
                                    item.is_video ? "--is-video" : "",
                                ])}
                            >
                                {imageUrl ? (
                                    <img
                                        alt=""
                                        src={imageUrl}
                                        loading="lazy"
                                        decoding="async"
                                    />
                                ) : null}
                                <div>
                                    <h2>{item.quote}</h2>
                                    <h4>
                                        {/* Legacy always rendered the icon
                                            `<img>` unconditionally (even with an
                                            empty src). Guard on the nullable
                                            `social_network_icon` object — when it
                                            exists we render the img (matching the
                                            legacy, with `src` falling back to ""
                                            if the rendition URL is empty); when
                                            it is null we skip it entirely. */}
                                        {icon ? (
                                            <img
                                                className="icon"
                                                src={iconUrl ?? ""}
                                                alt={icon.default_alt_text}
                                                loading="lazy"
                                                decoding="async"
                                            />
                                        ) : null}{" "}
                                        {item.username}
                                    </h4>
                                    <span>{t`View post`}</span>
                                </div>
                            </a>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};
